import React, { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Button from '../../../components/button/Button';
import { HEADING } from '../../../components/heading/constants/constants';
import Heading from '../../../components/heading/Heading';
import { VEC_ICON_NAME } from '../../../components/icons/constants';
import PasswordInput from '../../../components/input/passwordinput/PasswordInput';
import TextInput from '../../../components/input/textinput/TextInput';
import Loadable from '../../../components/loadable/Loadable';
import PreLoginLayout from '../components/preloginlayout/PreLoginLayout';
import { componentKey, updateAcceptInvitationFormFields, setIsAcceptInvitationSuccess, setSelectedCountry } from './MemberAcceptInvitationSlice';
import { componentKey as authenticationComponentKey } from '../../authentication/AuthenticationSlice';
import { componentKey as appLoaderComponentKey } from '../../loaders/apploader/AppLoaderSlice';
import styles from './MemberAcceptInvitation.styles';
import HorizontalSeparator from '../../../components/separator/horizontalSeparator/HorizontalSeparator';
import { BUTTON_TYPE } from '../../../components/button/constants';
import { PAGE_STATE } from '../../constants/constants';
import { addNotifications } from '../../toaster/ToasterPortalSlice';
import { TOASTER_VARIANT } from '../../../components/toast/constants';
import { submitAcceptInvitationRequest, validateToken } from './MemberAcceptInvitationSaga';
import { parse } from 'query-string';
import SelectDropdown from '../../../components/dropdown/selectdropdown/SelectDropdown';
import { countryCodes } from '../../../components/countrycodes/countrycodesdata';
import useGarbageCollector from '../../../../libs/garbagecollector/garbageCollectorHooks';
import { PAGE_PATH } from '../../dashboard/constants/constant';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { PRE_LOGIN_LOADING_STATES, PRE_LOGIN_STATES_MESSAGE_MAP } from '../constants';
import useTxNavigate from '../../../../libs/txnavigate/TxNavigateHook';
import General from '../../../../libs/utility/General';

export default function MemberAcceptInvitation() {
    
    useGarbageCollector(componentKey);
    
    const { loadingState, acceptInvitationFormFields, validResetToken, isAcceptInvitationSuccess, selectedCountry } = useSelector(state => state[componentKey]);
    const { isUserAuthenticated } = useSelector(state => state[authenticationComponentKey]);
    const { pageLoadingState } = useSelector((state) => state[appLoaderComponentKey]);

    const dispatch = useDispatch();
    const navigate = useTxNavigate();
    const { register, control, handleSubmit, watch, formState: {errors}, reset } = useForm();
    const location = useLocation();
    const queryParams = parse(location.search);
    const { executeRecaptcha } = useGoogleReCaptcha();


    const handleReCaptchaVerify = useCallback(async (executionTag) => {
        if (executeRecaptcha) {
            return await executeRecaptcha(executionTag);
        }
        
    }, [executeRecaptcha]);

    useEffect(() => {
        handleReCaptchaVerify();
    }, [handleReCaptchaVerify]);

    useEffect(() => {
        if(pageLoadingState === PAGE_STATE.PAGE_READY){
            if(queryParams?.email !== "" && queryParams?.email !== undefined && queryParams?.token !== "" && queryParams?.token !== undefined){
                handleReCaptchaVerify("validate_token").then((gToken) => 
                {
                    const token = General.handleGRecaptchaToken(gToken)
                    if(token) {
                        dispatch(validateToken({Email: queryParams.email, Token: queryParams.token, GRecaptchaResponse: token}))
                    }
                }
                )
            }else{
                dispatch(addNotifications({heading: "Error", message: "You have accessed an invalid url", variant: TOASTER_VARIANT.DANGER}));
            }
        }
    }, [handleReCaptchaVerify, pageLoadingState]);

    useEffect(() => {
        if(isUserAuthenticated){
            navigate(PAGE_PATH.DASHBOARD)
        }
    }, [isUserAuthenticated]);

    useEffect(() => {
        if(isAcceptInvitationSuccess){
            navigate(PAGE_PATH.DASHBOARD);
            dispatch(setIsAcceptInvitationSuccess(false));
        }
    }, [isAcceptInvitationSuccess]);

    const password = watch('Password');
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,}$/;
    const phoneNumberRegex = /^\d{10}$/;

    const validateConfirmPassword = (value) => {
        if (value !== password) {
        return "Passwords don't match";
        }
    }

    const handleInputChange = (e, onChangeValidationController, fieldType) => {
        
        const { name, value } = e.target;
        onChangeValidationController(value);
        let formFields = {};
        if(e.target.type === 'checkbox'){
            formFields = {
                ...acceptInvitationFormFields,
                [name]: (e.target.checked),
            }
        }else{
            formFields = {
                ...acceptInvitationFormFields,
                [name]: value,
            }
        }
        dispatch(updateAcceptInvitationFormFields(formFields));
    };

    const handleFormSubmit = (data) => {
        handleReCaptchaVerify("accept_invitation").then((gToken) => 
                {
                    const token = General.handleGRecaptchaToken(gToken)
                    if(token) {
                        dispatch(submitAcceptInvitationRequest({...acceptInvitationFormFields, GRecaptchaResponse: token}));
                    }
                })
        
    };

    const handleCountryCodeDropDownChange = (name, selectedOption) => {
        const formFields = { 
            ...acceptInvitationFormFields, 
            CountryCode: selectedOption.value,
        }
        dispatch(updateAcceptInvitationFormFields(formFields));
        dispatch(setSelectedCountry(selectedOption));
    }

    return (
        <PreLoginLayout>
            <Loadable
                currentState={loadingState.state}
                loadingStates={[PRE_LOGIN_LOADING_STATES.MEMBER_LOGIN_PAGE_LOADING]}
                messageMap={PRE_LOGIN_STATES_MESSAGE_MAP}
                isNewLoadable={true}
            >
                <div style={styles.loginForm}>
                    <Heading type={HEADING.H1}>Accept Invitation</Heading>
                    <form onSubmit={handleSubmit(handleFormSubmit)}>

                        {
                            validResetToken && <>
                                <div style={styles.fieldMarginTop}>
                                    <div style={styles.disabledText}>{acceptInvitationFormFields.Email}</div>
                                </div>

                                <div style={styles.fieldMarginTop}>
                                    <Controller
                                        control={control}
                                        name="Password"
                                        defaultValue={acceptInvitationFormFields.Password}
                                        rules={{ 
                                            required: "Password cannot be empty",
                                            pattern: {
                                                value: passwordRegex,
                                                message: "Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number and one special character"
                                            }
                                        }}
                                        render={({ field : {value, onChange} }) => (
                                            <PasswordInput 
                                                name="Password" 
                                                type="password" 
                                                placeHolder='Password' 
                                                value={value} 
                                                onChangeCb={(e) => handleInputChange(e, onChange, "password")} 
                                            />
                                        )}
                                    />
                                    {errors.password && <span style={styles.errorMessage}>{errors.password.message}</span>}
                                    <div style={styles.marginTopDependentField}>                        
                                        <Controller
                                            control={control}
                                            name="ConfirmPassword"
                                            defaultValue={acceptInvitationFormFields.ConfirmPassword}
                                            rules={{ 
                                                required: "Confirm password cannot be empty",
                                                validate: validateConfirmPassword,
                                            }}
                                            render={({ field : {value, onChange} }) => (
                                                <PasswordInput 
                                                    name="ConfirmPassword" 
                                                    type="password" 
                                                    placeHolder='Confirm password' 
                                                    value={value} 
                                                    onChangeCb={(e) => handleInputChange(e, onChange, "password")} 
                                                />
                                            )}
                                        />
                                        {errors.confirmPassword && <span style={styles.errorMessage}>{errors.confirmPassword.message}</span>}
                                    </div>
                                    <div style={{...styles.fieldDiv, ...styles.phoneFieldDiv}}>
                                        <div><label style={styles.fieldLabel}>Phone number</label></div>
                                        <div style={styles.phoneNumberRow}>
                                            <div style={styles.phoneDivWError}>
                                                <SelectDropdown
                                                    name="CountryCode"
                                                    value={{ label: selectedCountry.label, value: selectedCountry.value }}
                                                    prefixProps={{ text: selectedCountry.value }}
                                                    onSelectCb={(name, selectedOption) => handleCountryCodeDropDownChange(name, selectedOption)}
                                                    options={countryCodes}
                                                />
                                                <div style={styles.phoneNumberErrorDiv}></div>
                                            </div>
                                            <div style={styles.phoneDivWError}>
                                                <Controller
                                                    control={control}
                                                    name="PhoneNumber"
                                                    defaultValue={acceptInvitationFormFields.PhoneNumber}
                                                    rules={{ 
                                                        required: "Phone number cannot be empty",
                                                        pattern: {
                                                            value: phoneNumberRegex,
                                                            message: 'Invalid phone number format'
                                                        },
                                                    }}
                                                    render={({ field : {value, onChange} }) => (
                                                        <TextInput 
                                                            type={'text'}
                                                            placeHolder={'Phone number'}
                                                            name={'PhoneNumber'} 
                                                            value={value} 
                                                            onChangeCb={(e) => handleInputChange(e, onChange)} 
                                                        />
                                                    )}
                                                />
                                                <div style={styles.phoneNumberErrorDiv}>{errors.PhoneNumber && <span style={styles.errorMessage}>{errors.PhoneNumber.message}</span>}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div style={styles.submitButtonDiv}>
                                    <Button type={BUTTON_TYPE.PRIMARY}>
                                        Accept Invitation
                                    </Button>
                                </div>
                            </>
                        }
                    </form>
                </div>
            </Loadable>
        </PreLoginLayout>
    )
}